export const PHONE_COMPOSER_PLACEHOLDER = '0 (5xx) xxx xx xx';

type PhoneComposerInput = HTMLInputElement | HTMLTextAreaElement;

/** Keep phone characters only; nonstandard numbers remain editable for confirmation. */
export function formatComposerPhone(value: string): string {
  value = value.replace(/[^\d+(). -]/g, '');
  let digits = value.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('905')) digits = `0${digits.slice(2)}`;
  else if (digits.startsWith('5')) digits = `0${digits}`;
  if (!digits.startsWith('0') || digits.length > 11) return value;
  if (digits.length === 1) return digits;
  let formatted = `0 (${digits.slice(1, 4)}`;
  if (digits.length >= 4) formatted += ')';
  if (digits.length > 4) formatted += ` ${digits.slice(4, 7)}`;
  if (digits.length > 7) formatted += ` ${digits.slice(7, 9)}`;
  if (digits.length > 9) formatted += ` ${digits.slice(9, 11)}`;
  return formatted;
}

export function mountPhoneComposer(input: PhoneComposerInput): () => void {
  const attributes = ['inputmode', 'autocomplete'] as const;
  const previous = attributes.map((name) => input.getAttribute(name));
  input.setAttribute('inputmode', 'tel');
  input.setAttribute('autocomplete', 'tel-national');

  const formatInput = (event?: Event) => {
    if ((event as InputEvent | undefined)?.isComposing) return;
    const raw = input.value;
    const formatted = formatComposerPhone(raw);
    if (formatted === raw) return;
    const start = input.selectionStart ?? raw.length;
    const end = input.selectionEnd ?? start;
    const digitDelta = formatted.replace(/\D/g, '').length - raw.replace(/\D/g, '').length;
    const mapCaret = (position: number): number => {
      if (position === raw.length) return formatted.length;
      if (position === 0) return 0;
      const count = Math.max(0, raw.slice(0, position).replace(/\D/g, '').length + digitDelta);
      let seen = 0;
      for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i]!) && ++seen === count) return i + 1;
      }
      return 0;
    };
    input.value = formatted;
    input.setSelectionRange(mapCaret(start), mapCaret(end));
  };

  const onBeforeInput = (event: Event) => {
    const { inputType, data, isComposing } = event as InputEvent;
    if (!isComposing && inputType === 'insertText' && data && /[^\d+(). -]/.test(data)) {
      event.preventDefault();
      return;
    }
    if (inputType !== 'deleteContentBackward' && inputType !== 'deleteContentForward') return;
    if (input.selectionStart !== input.selectionEnd || !/^[\d()\s]*$/.test(input.value)) return;
    const caret = input.selectionStart;
    if (caret === null) return;
    const backward = inputType === 'deleteContentBackward';
    let index = backward ? caret - 1 : caret;
    if (index < 0 || index >= input.value.length || /\d/.test(input.value[index]!)) return;
    while (index >= 0 && index < input.value.length && !/\d/.test(input.value[index]!)) {
      index += backward ? -1 : 1;
    }
    if (index < 0 || index >= input.value.length) return;
    event.preventDefault();
    input.setRangeText('', backward ? index : caret, backward ? caret : index + 1, 'start');
    // Programmatic edits must notify the SDK's composer state as well.
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  };

  const onBeforeSubmit = (event: Event) => {
    formatInput();
    const digits = input.value.replace(/\D/g, '');
    if (!digits) {
      event.preventDefault();
      return;
    }
  };
  const onCompositionEnd = () => {
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  };

  input.addEventListener('input', formatInput, true);
  input.addEventListener('beforeinput', onBeforeInput);
  input.addEventListener('compositionend', onCompositionEnd);
  input.addEventListener('gengage:composer:before-submit', onBeforeSubmit);
  formatInput();
  return () => {
    input.removeEventListener('input', formatInput, true);
    input.removeEventListener('beforeinput', onBeforeInput);
    input.removeEventListener('compositionend', onCompositionEnd);
    input.removeEventListener('gengage:composer:before-submit', onBeforeSubmit);
    attributes.forEach((name, index) => {
      const value = previous[index];
      if (value == null) input.removeAttribute(name);
      else input.setAttribute(name, value);
    });
  };
}
